'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    getGuides,
    TissGuide,
    GUIDE_STATUS_LABELS,
    GUIDE_STATUS_COLORS,
    GUIDE_TYPE_LABELS,
    GUIDE_ORIGIN_LABELS,
    GuideStatus,
} from '@/lib/api/billing/guide-api'
import {
    MdDescription,
    MdSearch,
    MdClose,
    MdAdd,
    MdArrowBack,
    MdPerson,
    MdLocalHospital,
    MdAccessTime,
    MdArrowForward,
} from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function GuidesPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('billing', 'guides')
    const [guides, setGuides] = useState<TissGuide[]>([])
    const [loading, setLoading] = useState(true)
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [typeFilter, setTypeFilter] = useState<string>('all')

    useEffect(() => {
        if (user?.companyId) {
            loadGuides()
        }
    }, [user?.companyId])

    const loadGuides = async () => {
        setLoading(true)
        try {
            const response = await getGuides()
            if (response.success) {
                setGuides(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar guias')
            }
        } catch (error) {
            toast.error('Erro ao carregar guias')
        } finally {
            setLoading(false)
        }
    }

    const filteredGuides = guides.filter(guide => {
        if (searchTerm) {
            const search = searchTerm.toLowerCase()
            if (
                !guide.guideNumber.toLowerCase().includes(search) &&
                !guide.client?.name?.toLowerCase().includes(search) &&
                !guide.healthPlan?.name?.toLowerCase().includes(search)
            ) {
                return false
            }
        }
        if (statusFilter !== 'all' && guide.status !== statusFilter) {
            return false
        }
        if (typeFilter !== 'all' && guide.guideType !== typeFilter) {
            return false
        }
        return true
    })

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando guias...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdDescription className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Guias TISS
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {guides.length} {guides.length === 1 ? 'guia' : 'guias'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar guias..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    setSearchTerm('')
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <button
                                    onClick={() => navigate('/billing/guides/new')}
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">Nova Guia</span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap">
                <button
                    onClick={() => setStatusFilter('all')}
                    className={`btn btn-sm ${statusFilter === 'all' ? 'btn-secondary' : 'btn-ghost'}`}
                >
                    Todas
                </button>
                <button
                    onClick={() => setStatusFilter('GUIDE_DRAFT')}
                    className={`btn btn-sm ${statusFilter === 'GUIDE_DRAFT' ? 'btn-ghost' : 'btn-ghost'}`}
                >
                    Rascunho
                </button>
                <button
                    onClick={() => setStatusFilter('GUIDE_PENDING_AUTHORIZATION')}
                    className={`btn btn-sm ${statusFilter === 'GUIDE_PENDING_AUTHORIZATION' ? 'btn-warning' : 'btn-ghost'}`}
                >
                    Aguardando
                </button>
                <button
                    onClick={() => setStatusFilter('GUIDE_AUTHORIZED')}
                    className={`btn btn-sm ${statusFilter === 'GUIDE_AUTHORIZED' ? 'btn-success' : 'btn-ghost'}`}
                >
                    Autorizadas
                </button>
                <button
                    onClick={() => setStatusFilter('GUIDE_SUBMITTED')}
                    className={`btn btn-sm ${statusFilter === 'GUIDE_SUBMITTED' ? 'btn-info' : 'btn-ghost'}`}
                >
                    Enviadas
                </button>
                <button
                    onClick={() => setStatusFilter('GUIDE_GLOSS')}
                    className={`btn btn-sm ${statusFilter === 'GUIDE_GLOSS' ? 'btn-error' : 'btn-ghost'}`}
                >
                    Glosadas
                </button>
            </div>

            {/* Type Filter */}
            <div className="flex gap-4 flex-wrap">
                <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="select select-bordered select-sm"
                >
                    <option value="all">Todos os Tipos</option>
                    <option value="CONSULTA">Consulta</option>
                    <option value="SADT">SP/SADT</option>
                    <option value="INTERNACAO">Internação</option>
                    <option value="HONORARIOS">Honorários</option>
                </select>
            </div>

            {filteredGuides.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdDescription className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma guia encontrada
                    </h3>
                    <p className="text-base-content/60 mb-6">
                        Crie sua primeira guia TISS
                    </p>
                    {canCreate && (
                        <button
                            onClick={() => navigate('/billing/guides/new')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Nova Guia
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredGuides.map(guide => (
                        <div
                            key={guide.id}
                            onClick={() => navigate(`/billing/guides/${guide.id}`)}
                            className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span
                                            className={`badge badge-sm ${GUIDE_STATUS_COLORS[guide.status]}`}
                                        >
                                            {GUIDE_STATUS_LABELS[guide.status]}
                                        </span>
                                        <span className="badge badge-sm badge-ghost">
                                            {GUIDE_TYPE_LABELS[guide.guideType]}
                                        </span>
                                    </div>
                                    <h3 className="font-semibold text-base-content mb-1">
                                        {guide.guideNumber}
                                    </h3>
                                    {guide.authorizationNumber && (
                                        <p className="text-xs text-base-content/60">
                                            Aut: {guide.authorizationNumber}
                                        </p>
                                    )}
                                </div>
                                <MdArrowForward className="text-base-content/40 flex-shrink-0 ml-2" />
                            </div>

                            <div className="space-y-2 mb-3">
                                {guide.client && (
                                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                                        <MdPerson className="text-base" />
                                        <span className="truncate">
                                            {guide.client.name}
                                        </span>
                                    </div>
                                )}
                                {guide.healthPlan && (
                                    <div className="flex items-center gap-2 text-sm text-base-content/60">
                                        <MdLocalHospital className="text-base" />
                                        <span className="truncate">
                                            {guide.healthPlan.name}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                <div className="flex items-center gap-2 text-xs text-base-content/60">
                                    <MdAccessTime className="text-sm" />
                                    <span>
                                        {format(
                                            new Date(guide.requestDate),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )}
                                    </span>
                                </div>
                                <span className="text-sm font-semibold text-secondary">
                                    {formatCurrency(guide.totalRequested)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
